import { HaveListRow } from 'types/interfaces/collectionList';
import { decimalToPoints } from 'utils/decimalUtils';
import { CardFinish, getAvailableFinishes } from 'utils/finishes';

/**
 * One selectable printing. Shared by the have-list edit modal and the offer
 * builder's un-catalogued add, so both price and sort printings identically.
 */
export interface PrintingOption {
  id: number;
  setCode: string;
  setName: string;
  collectorNumber: string;
  lang: string;
  releasedAt: string;
  scryfallID: string;
  pointsNonFoil: number | null;
  pointsFoil: number | null;
  pointsEtched: number | null;
  /** Finishes this printing exists in (lowercase, from the one helper) */
  finishes: CardFinish[];
}

/**
 * Normalize the /cards/:id/printings payload. The backend is inconsistent
 * about `scryfallID` vs `scryfallId` casing, so both are read.
 */
export const toPrintingOptions = (printings: unknown): PrintingOption[] => {
  if (!Array.isArray(printings)) return [];
  return (printings as any[])
    .map((p: any) => {
      const priceData = Array.isArray(p.price) ? p.price[0] : p.price;
      return {
        id: p.id,
        setCode: p.setCode || '',
        setName: p.setName || p.setCode || '',
        collectorNumber: p.collectorNumber || '',
        lang: p.lang || 'en',
        releasedAt: p.releasedAt || '',
        scryfallID: p.scryfallID || p.scryfallId || '',
        pointsNonFoil: priceData?.usd != null ? decimalToPoints(priceData.usd) : null,
        pointsFoil: priceData?.usdFoil != null ? decimalToPoints(priceData.usdFoil) : null,
        pointsEtched: priceData?.usdEtched != null ? decimalToPoints(priceData.usdEtched) : null,
        finishes: getAvailableFinishes(p),
      };
    })
    .sort((a, b) => {
      // Primary: release date descending (newest first)
      if (a.releasedAt && b.releasedAt) {
        const dateDiff = new Date(b.releasedAt).getTime() - new Date(a.releasedAt).getTime();
        if (dateDiff !== 0) return dateDiff;
      }
      // Secondary: set name alphabetical
      const nameDiff = a.setName.localeCompare(b.setName);
      if (nameDiff !== 0) return nameDiff;
      // Tertiary: collector number (natural sort)
      return a.collectorNumber.localeCompare(b.collectorNumber, undefined, { numeric: true });
    });
};

/** Free-text filter over set name, set code, and collector number. */
export const filterPrintings = (options: PrintingOption[], searchTerm: string): PrintingOption[] => {
  if (!searchTerm) return options;
  const lower = searchTerm.toLowerCase();
  return options.filter(
    (p) =>
      p.setName.toLowerCase().includes(lower) ||
      p.setCode.toLowerCase().includes(lower) ||
      p.collectorNumber.toLowerCase().includes(lower)
  );
};

/**
 * Narrow the printing list to what a want record would actually accept, in the
 * same tier order the backend matcher uses:
 *
 *   1. `acceptedPrintingIds` — the want names exact printings
 *   2. `acceptableSetCodes` / `acceptableSets` — the want names sets
 *   3. neither (a genuine "Any set" want) — every printing is acceptable
 *
 * A sender must never be offered a printing the want would reject: picking one
 * would create a have row that cannot legally be part of the offer, failing at
 * send time with a condition/printing error instead of at selection time.
 *
 * Language is deliberately NOT filtered here — the want's `acceptableLanguages`
 * constrains it, but a printing row IS a language, so language narrowing is
 * applied by the caller alongside its own language control.
 */
export const constrainToWant = (
  options: PrintingOption[],
  want: Pick<
    HaveListRow,
    'acceptedPrintingIds' | 'acceptableSetCodes' | 'acceptableSets' | 'isAnySet'
  >
): PrintingOption[] => {
  const printingIds = want.acceptedPrintingIds;
  if (!want.isAnySet && Array.isArray(printingIds) && printingIds.length > 0) {
    const allowed = new Set(printingIds);
    const narrowed = options.filter((p) => allowed.has(p.id));
    // An empty result means the want pins printings this card list doesn't
    // contain; fall through rather than render an unusable empty picker.
    if (narrowed.length > 0) return narrowed;
  }

  const setCodes = want.acceptableSetCodes ?? want.acceptableSets;
  if (!want.isAnySet && Array.isArray(setCodes) && setCodes.length > 0) {
    const allowed = new Set(setCodes.map((c) => c.toLowerCase()));
    const narrowed = options.filter((p) => allowed.has(p.setCode.toLowerCase()));
    if (narrowed.length > 0) return narrowed;
  }

  return options;
};

/**
 * Which finishes a printing exists in, canonical order (the one helper reads the
 * printing's finishes array first, then the backend flags, then price presence).
 * Empty when nothing is selected yet.
 */
export const finishAvailability = (option: PrintingOption | undefined): CardFinish[] =>
  option ? option.finishes : [];

/** Points for one finish of a printing, from that finish's own column; null renders "???". */
export const pointsForOption = (option: PrintingOption | undefined, finish: CardFinish): number | null => {
  if (!option) return null;
  const points = finish === 'etched' ? option.pointsEtched : finish === 'foil' ? option.pointsFoil : option.pointsNonFoil;
  return points != null && points > 0 ? points : null;
};
